import React, { useState, useEffect, useCallback, useRef } from 'react'
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker, InfoWindow } from '@react-google-maps/api'
import PropTypes from 'prop-types'

// Your Google Maps API key
const GOOGLE_MAPS_API_KEY = 'AIzaSyAvDka_HEWufYVqW_m2KzIfSJEjd9O-gYI'

// The fixed starting and ending address
const START_END_ADDRESS = "Psichari 1, Metamorfosi 144 52, Greece"

const containerStyle = {
  width: '100%',
  height: '350px'
}

const defaultCenter = {
  lat: 37.9838,
  lng: 23.7275
}

const OptimalRouteMap = ({ addresses, onRouteCalculated, Dontshow = false }) => {
  const [directions, setDirections] = useState(null)
  const [totalDistance, setTotalDistance] = useState('')
  const [totalDuration, setTotalDuration] = useState('')
  const [routeDetails, setRouteDetails] = useState([])
  const [waypointOrder, setWaypointOrder] = useState([])
  const [selectedMarker, setSelectedMarker] = useState(null)
  const calculatedRef = useRef(false)
  const addressesRef = useRef(addresses)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  })

  const calculateRoute = useCallback(() => {
    if (!window.google || addresses.length < 1) {
      return
    }

    const allAddresses = [START_END_ADDRESS, ...addresses]
    const directionsService = new window.google.maps.DirectionsService()

    const origin = allAddresses[0]
    const destination = START_END_ADDRESS
    const waypoints = allAddresses.slice(1).map(address => ({
      location: address,
      stopover: true,
    }))

    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: 'bestguess',
        },
      },
      (result, status) => {
        if (status === 'OK') {
          setDirections(result)
          const route = result.routes[0]

          // Get the optimized waypoint order
          const optimizedOrder = route.waypoint_order || []
          setWaypointOrder(optimizedOrder)

          let totalDist = 0
          let totalDur = 0
          const details = route.legs.map((leg, index) => {
            totalDist += leg.distance.value
            totalDur += leg.duration_in_traffic ? leg.duration_in_traffic.value : leg.duration.value
            return {
              start: leg.start_address,
              end: leg.end_address,
              startLocation: leg.start_location,
              endLocation: leg.end_location,
              distance: leg.distance.text,
              duration: leg.duration_in_traffic ? leg.duration_in_traffic.text : leg.duration.text,
              // Label: Start for first, then A, B, C... for waypoints, End for last
              label: index === 0 ? 'Start' : String.fromCharCode(64 + index), // A=65, so 64+1=A
            }
          })

          setTotalDistance((totalDist / 1000).toFixed(2))
          setTotalDuration(Math.floor(totalDur / 60))
          setRouteDetails(details)

          if (onRouteCalculated) {
            onRouteCalculated(details, (totalDist / 1000).toFixed(2), Math.floor(totalDur / 60))
          }
        } else {
          console.error(`Error calculating directions: ${status}`)
        }
      }
    )
  }, [addresses, onRouteCalculated])

  useEffect(() => {
    // Only recalculate if addresses actually changed
    const addressesChanged = JSON.stringify(addresses) !== JSON.stringify(addressesRef.current)

    if (isLoaded && addresses.length > 0 && (!calculatedRef.current || addressesChanged)) {
      calculatedRef.current = true
      addressesRef.current = addresses
      calculateRoute()
    }
  }, [isLoaded, addresses, calculateRoute])

  const openGoogleMaps = () => {
    if (routeDetails.length === 0) {
      console.error("Route details are empty")
      return
    }

    const baseUrl = "https://www.google.com/maps/dir/?api=1"
    const origin = encodeURIComponent(START_END_ADDRESS)
    const destination = encodeURIComponent(START_END_ADDRESS)
    const waypoints = routeDetails.map(leg => encodeURIComponent(leg.end)).join("|")

    const mapsUrl = `${baseUrl}&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`

    window.open(mapsUrl, "_blank")
  }

  // Generate markers for the route
  const getMarkers = () => {
    if (!directions || routeDetails.length === 0) return []

    const markers = []
    const route = directions.routes[0]

    // Starting point marker (green)
    if (route.legs[0]) {
      markers.push({
        position: {
          lat: route.legs[0].start_location.lat(),
          lng: route.legs[0].start_location.lng()
        },
        label: 'S',
        title: 'Starting Point',
        address: route.legs[0].start_address,
        color: '#16a34a', // Green
        isStart: true
      })
    }

    // Waypoint markers (A, B, C, etc.) in optimized order
    route.legs.forEach((leg, index) => {
      if (index < route.legs.length - 1) { // Skip the last leg's end (it's the return to start)
        markers.push({
          position: {
            lat: leg.end_location.lat(),
            lng: leg.end_location.lng()
          },
          label: String.fromCharCode(65 + index), // A, B, C...
          title: `Stop ${String.fromCharCode(65 + index)}`,
          address: leg.end_address,
          color: '#3b82f6', // Blue
          isStart: false,
          distance: leg.distance.text,
          duration: leg.duration_in_traffic ? leg.duration_in_traffic.text : leg.duration.text
        })
      }
    })

    return markers
  }

  if (!isLoaded) {
    return <div>Loading map...</div>
  }

  const markers = getMarkers()

  return (
    <div>
      {/* Display total distance and time */}
      {totalDistance.length > 0 && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Optimal Route Summary
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#374151' }}>
            <span><strong>Distance:</strong> {totalDistance} km</span>
            <span><strong>Duration:</strong> {totalDuration} min</span>
          </div>
        </div>
      )}

      {!Dontshow && addresses.length > 0 && (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={defaultCenter}
          zoom={12}
        >
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true, // We'll add our own markers
                polylineOptions: {
                  strokeColor: '#3b82f6',
                  strokeWeight: 4
                }
              }}
            />
          )}

          {/* Custom markers */}
          {markers.map((marker, index) => (
            <Marker
              key={index}
              position={marker.position}
              label={{
                text: marker.label,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '12px'
              }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 14,
                fillColor: marker.color,
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 2,
              }}
              title={marker.title}
              onClick={() => setSelectedMarker(marker)}
            />
          ))}

          {/* Info window for selected marker */}
          {selectedMarker && (
            <InfoWindow
              position={selectedMarker.position}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div style={{ padding: '4px', maxWidth: '200px' }}>
                <div style={{ fontWeight: 600, marginBottom: '4px', color: selectedMarker.isStart ? '#16a34a' : '#3b82f6' }}>
                  {selectedMarker.isStart ? 'Starting Point' : `Stop ${selectedMarker.label}`}
                </div>
                <div style={{ fontSize: '12px', color: '#374151' }}>
                  {selectedMarker.address}
                </div>
                {!selectedMarker.isStart && selectedMarker.distance && (
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    {selectedMarker.distance} • {selectedMarker.duration}
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}

      {/* Button to open in Google Maps */}
      {Dontshow && (
        <button onClick={openGoogleMaps} className="btn btn-primary">
          Open Google Maps
        </button>
      )}

      {/* List of route details with labels */}
      {!Dontshow && routeDetails.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            Route Order
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Starting point */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#dcfce7', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#16a34a',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 600
              }}>
                S
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', fontWeight: 500, color: '#166534' }}>Starting Point</div>
                <div style={{ fontSize: '11px', color: '#15803d' }}>{routeDetails[0]?.start}</div>
              </div>
            </div>

            {/* Waypoints A, B, C... */}
            {routeDetails.slice(0, -1).map((leg, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {String.fromCharCode(65 + index)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#111827' }}>{leg.end}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{leg.distance} • {leg.duration}</div>
                </div>
              </div>
            ))}

            {/* Return to start */}
            {routeDetails.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fde68a' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 600
                }}>
                  ↩
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#92400e' }}>Return to Start</div>
                  <div style={{ fontSize: '11px', color: '#b45309' }}>
                    {routeDetails[routeDetails.length - 1]?.distance} • {routeDetails[routeDetails.length - 1]?.duration}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

OptimalRouteMap.propTypes = {
  addresses: PropTypes.arrayOf(PropTypes.string).isRequired,
  Dontshow: PropTypes.bool,
  onRouteCalculated: PropTypes.func,
}

export default OptimalRouteMap
